(ns ui.components.input.number
  (:require
   [clojure.string :as string]
   [common.number-format :as number-format]
   [common.util :as cu]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.constants :as constants]
   [ui.dom-globals :as dg]
   [ui.util :as util]))

(def default-min-decimals 0)
(def default-max-decimals 2)

(def en-format
  (let [NumberFormat (dg/number-format)]
    (NumberFormat. "en-US" {})))

(defn en-format-with-decimals
  [min-decimals max-decimals]
  (let [NumberFormat (dg/number-format)]
    (NumberFormat. "en-US" (clj->js {:minimumFractionDigits min-decimals
                                     :maximumFractionDigits max-decimals}))))

(defn format-number
  [{:keys [number-format minimum-fraction-digits maximum-fraction-digits amount]}]
  (if (and en-format number-format)
    (let [en-format (if minimum-fraction-digits
                      (en-format-with-decimals minimum-fraction-digits maximum-fraction-digits)
                      en-format)
          thousand-separator (if number-format
                               (number-format/thousand-separator number-format)
                               constants/thousand-separator)
          decimal-separator (if number-format
                              (number-format/decimal-separator number-format)
                              constants/decimal-separator)
          res (-> (oops/ocall! en-format "format" amount)
                  (string/replace #"\." "[")                                                        ;; TEMP REPLACE to make sure decimal is not '.'
                  (string/replace #"\," (or thousand-separator ""))
                  (string/replace #"\[" decimal-separator))]
      res)
    (oops/ocall (js/Number. amount) "toLocaleString"
                js/undefined
                (clj->js {:minimumFractionDigits minimum-fraction-digits
                          :maximumFractionDigits maximum-fraction-digits}))))

(defn format-value
  [{:keys [focused?
           number-format
           min-decimals
           max-decimals
           multiplier
           percentage-mode?
           multiplier-mode?]} float-value]
  (let [minimum-fraction-digits (if focused? 0 min-decimals)
        maximum-fraction-digits (if focused? 20 max-decimals)
        divider (util/multiplier-str->divider multiplier)
        should-divide? (when (or (float? float-value)
                                 (int? float-value))
                         (<= divider (abs float-value)))
        formatter (fn [amount]
                    (format-number {:number-format             number-format
                                    :minimum-fraction-digits minimum-fraction-digits
                                    :maximum-fraction-digits maximum-fraction-digits
                                    :amount                   amount}))
        formatted (when (or (float? float-value)
                            (int? float-value))
                    (cond-> (str float-value)
                      true (js/Number.)
                      should-divide? (/ divider)
                      true formatter
                      should-divide? (str multiplier)
                      percentage-mode? (str "%")
                      multiplier-mode? (str "x")))]
    formatted))

(defn parse-value
  [str-value number-format]
  (let [float-meta (util/parse-float-info (if number-format
                                            (number-format/decimal-separator number-format)
                                            constants/decimal-separator)
                                          str-value nil)
        {:keys [float-value]} float-meta
        is-within-bounds? (and (number? float-value)
                               (< constants/MIN-SAFE-INTEGER float-value constants/MAX-SAFE-INTEGER))]
    (if-not is-within-bounds?
      nil
      float-value)))

(defn safe-number-value?
  [value]
  (or (nil? value)
      (= value "")
      (and (number? value)
           (<= constants/MIN-SAFE-INTEGER value constants/MAX-SAFE-INTEGER))))

(defn clamp-to-bounds
  [{:keys [min max]} value]
  (cond
    (nil? value) nil
    (= value "") ""
    (and (number? min) (< value min)) min
    (and (number? max) (< max value)) max
    :else value))

(defn format-focused
  [{:keys [edit-value edit-negative? edit-decimals decimal-separator number-format]}]
  (when edit-value
    (if (= edit-value "")
      (str (when edit-negative? "-")
           (when (= edit-decimals 0)
             decimal-separator))
      (let [formatted-w-separators (util/format-with-separators number-format
                                                                edit-value
                                                                edit-decimals
                                                                edit-negative?
                                                                15)]
        formatted-w-separators))))

(defn view
  [{:keys [_id _value _min-decimals _max-decimals
           _multiplier _percentage-mode? _multiplier-mode? _auto-focus? _input-ref
           _on-change _disabled? _on-focus _on-blur _variant _placeholder
           _number-format _transparent? _size _class _min _max]}]
  (let [state-atom (r/atom {:focused? false})]
    (fn
      [{:keys [id value min-decimals max-decimals
               multiplier percentage-mode? multiplier-mode? auto-focus? input-ref
               on-change disabled? on-focus on-blur variant placeholder
               number-format transparent? size class]
        :as props}]

      (let [{:keys [focused? selection-start selection-end]} @state-atom
            max-decimals (or max-decimals default-max-decimals)
            min-decimals (min (or min-decimals default-min-decimals) max-decimals)
            min-bound (:min props)
            max-bound (:max props)

            multiplier (or multiplier "")

            value (if (util/is-bigdec? value)
                    (util/bigdec->float value)
                    value)
            temp-value (:temp-value @state-atom)
            edit-decimals (if (nil? temp-value)
                            -1
                            (get @state-atom :decimal-count))
            edit-negative? (get @state-atom :negative?)

            edit-value (or temp-value
                           (when edit-negative? "-"))
            [thousand-separator decimal-separator] (if (nil? number-format)
                                                     [constants/thousand-separator constants/decimal-separator]
                                                     (number-format/separators-as-strings number-format))
            display-str (or (if focused?
                              (format-focused {:edit-value        edit-value
                                               :edit-negative?    edit-negative?
                                               :edit-decimals     edit-decimals
                                               :decimal-separator decimal-separator
                                               :number-format     number-format})
                              (format-value
                               {:focused?         focused?
                                :min-decimals     min-decimals
                                :max-decimals     max-decimals
                                :multiplier       multiplier
                                :number-format    number-format
                                :percentage-mode? percentage-mode?
                                :multiplier-mode? multiplier-mode?}
                               value))
                            "")]

        [:input
         (merge
          {:type        "text"
           :ref         (or input-ref
                            (fn [input-ref]
                              (when input-ref
                                (when (:focused? @state-atom)
                                  (oops/ocall input-ref "setSelectionRange" selection-start selection-end))
                                (when-not (:initialized? @state-atom)
                                  (swap! state-atom assoc :initialized? true)
                                  (when auto-focus?
                                    (oops/ocall input-ref "focus"))))))
           :placeholder (or placeholder "")
           :class-name  (util/cm "peer px-3 text-md h-10 rounded placeholder-transparent focus:outline-none focus:ring-0 placeholder:text-base-light-text-paler"
                                 "text-left"
                                 (if transparent?
                                   "bg-transparent disabled:bg-transparent"
                                   "text-input-light-text disabled:text-disabled-light-text
                                    bg-input-light disabled:bg-disabled-light")
                                 (if (not= variant :borderless)
                                   "py-2 border border-solid border-input-light-border-paler hover:border-input-light-border focus:border-input-light-border"
                                   "py-2 border-none focus:border-transparent ")
                                 (when-not (cu/blank-value? value)
                                   "value")
                                 class)
           :value       display-str
           ; When focused, external value change should not affect local state
           :on-focus    (fn [_e]
                          (let [float-value (util/parse-float-info "." (str value) "")]
                            (swap! state-atom assoc
                                   :focused? true
                                   :decimal-count (or (:decimal-count float-value) -1)
                                   :negative? (or (:negative? float-value) false)
                                   :temp-value (:float-value float-value)))

                          (when (fn? on-focus)
                            (on-focus)))

           :on-blur     (fn [_e]
                          (when (fn? on-blur)
                            (if (or (int? temp-value)
                                    (float? temp-value))
                              (on-blur (clamp-to-bounds {:min min-bound
                                                         :max max-bound}
                                                        temp-value))
                              (on-blur nil)))
                          (swap! state-atom dissoc
                                 :focused?
                                 :temp-value
                                 :decimal-count
                                 :negative?))

           :on-change   (fn [e]
                          (let [input (dg/event-target-value e)
                                value-str (str input)
                                float-info (util/parse-float-info decimal-separator input "")
                                float-value (:float-value float-info)
                                formatted-value (format-focused {:edit-value        float-value
                                                                 :edit-negative?    edit-negative?
                                                                 :edit-decimals     edit-decimals
                                                                 :decimal-separator decimal-separator
                                                                 :number-format     number-format})
                                valid-edit-value? (safe-number-value? float-value)
                                value-separator-count (->> value-str
                                                           (filter (fn [c]
                                                                     (= (str c) thousand-separator)))
                                                           (count))
                                new-separator-count (->> formatted-value
                                                         (filter (fn [c]
                                                                   (= (str c) thousand-separator)))
                                                         (count))
                                target (dg/target e)]
                            (when valid-edit-value?
                              (swap! state-atom assoc
                                     :selection-start (+ (oops/oget target "selectionStart")
                                                         (- new-separator-count value-separator-count))
                                     :selection-end (+ (oops/oget target "selectionEnd")
                                                       (- new-separator-count value-separator-count))
                                     :decimal-count (or (:decimal-count float-info) -1)
                                     :negative? (or (:negative? float-info) false)
                                     :temp-value float-value)
                              (when (fn? on-change)
                                (on-change float-value)))))}
          (when id
            {:id id})
          (when disabled?
            {:disabled true})
          (when size
            {:size size}))]))))
